import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterPanel = ({ 
  filters, 
  onFiltersChange, 
  onClearFilters, 
  isCollapsed, 
  onToggle,
  categories,
  centers,
  userTier 
}) => {
  const [priceRange, setPriceRange] = useState(filters?.priceRange || [0, 500]);

  const handleCategoryChange = (category, checked) => {
    const updatedCategories = checked
      ? [...(filters?.categories || []), category]
      : (filters?.categories || [])?.filter(c => c !== category);
    
    onFiltersChange({ ...filters, categories: updatedCategories });
  };

  const handleCenterChange = (center, checked) => {
    const updatedCenters = checked
      ? [...(filters?.centers || []), center]
      : (filters?.centers || [])?.filter(c => c !== center);
    
    onFiltersChange({ ...filters, centers: updatedCenters });
  };

  const handlePriceRangeChange = (newRange) => {
    setPriceRange(newRange);
    onFiltersChange({ ...filters, priceRange: newRange });
  };

  const handleAvailabilityChange = (availability, checked) => {
    const updatedAvailability = checked
      ? [...(filters?.availability || []), availability]
      : (filters?.availability || [])?.filter(a => a !== availability);
    
    onFiltersChange({ ...filters, availability: updatedAvailability });
  };

  const handleRatingChange = (rating) => {
    onFiltersChange({ ...filters, minRating: rating });
  };

  const activeFiltersCount = [
    filters?.categories?.length || 0,
    filters?.centers?.length || 0,
    filters?.availability?.length || 0,
    filters?.minRating ? 1 : 0,
    filters?.priceRange ? 1 : 0
  ]?.reduce((sum, count) => sum + count, 0);

  if (isCollapsed) {
    return (
      <div className="w-12 bg-card border-r border-border p-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="w-full mb-4"
        >
          <Icon name="Filter" size={20} />
        </Button>
        {activeFiltersCount > 0 && (
          <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-medium mx-auto">
            {activeFiltersCount}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-80 bg-card border-r border-border h-full overflow-y-auto">
      {/* Filter Header */}
      <div className="sticky top-0 bg-card border-b border-border p-4 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Filter" size={20} className="text-primary" />
            <h2 className="font-heading font-semibold text-foreground">Filters</h2>
            {activeFiltersCount > 0 && (
              <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
                {activeFiltersCount}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
          >
            <Icon name="X" size={16} />
          </Button>
        </div>
        
        {activeFiltersCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClearFilters}
            className="w-full mt-3"
            iconName="RotateCcw"
            iconPosition="left"
          >
            Clear All Filters
          </Button>
        )}
      </div>
      <div className="p-4 space-y-6">
        {/* Search */}
        <div>
          <Input
            type="search"
            placeholder="Search products..."
            value={filters?.search || ''}
            onChange={(e) => onFiltersChange({ ...filters, search: e?.target?.value })}
            className="w-full"
          />
        </div>

        {/* Categories */}
        <div>
          <h3 className="font-heading font-medium text-foreground mb-3">Categories</h3>
          <div className="space-y-2">
            {categories?.map((category) => (
              <Checkbox
                key={category?.id}
                label={`${category?.name} (${category?.count})`}
                checked={filters?.categories?.includes(category?.id) || false}
                onChange={(e) => handleCategoryChange(category?.id, e?.target?.checked)}
              />
            ))}
          </div>
        </div>

        {/* Price Range */}
        <div>
          <h3 className="font-heading font-medium text-foreground mb-3">Price Range</h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Input
                type="number"
                placeholder="Min"
                value={priceRange?.[0]}
                onChange={(e) => handlePriceRangeChange([parseInt(e?.target?.value) || 0, priceRange?.[1]])}
                className="flex-1"
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="number"
                placeholder="Max"
                value={priceRange?.[1]}
                onChange={(e) => handlePriceRangeChange([priceRange?.[0], parseInt(e?.target?.value) || 500])}
                className="flex-1"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              ${priceRange?.[0]} - ${priceRange?.[1]}
            </div>
          </div>
        </div>

        {/* Wellness Centers */}
        <div>
          <h3 className="font-heading font-medium text-foreground mb-3">Wellness Centers</h3>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {centers?.map((center) => (
              <Checkbox
                key={center?.id}
                label={`${center?.name} (${center?.productCount})`}
                checked={filters?.centers?.includes(center?.id) || false}
                onChange={(e) => handleCenterChange(center?.id, e?.target?.checked)}
              />
            ))}
          </div>
        </div>

        {/* Availability */}
        <div>
          <h3 className="font-heading font-medium text-foreground mb-3">Availability</h3>
          <div className="space-y-2">
            {[
              { id: 'available', label: 'In Stock', icon: 'CheckCircle' },
              { id: 'limited', label: 'Limited Stock', icon: 'AlertCircle' },
              { id: 'unavailable', label: 'Out of Stock', icon: 'XCircle' }
            ]?.map((availability) => (
              <Checkbox
                key={availability?.id}
                label={
                  <div className="flex items-center space-x-2">
                    <Icon name={availability?.icon} size={14} />
                    <span>{availability?.label}</span>
                  </div>
                }
                checked={filters?.availability?.includes(availability?.id) || false}
                onChange={(e) => handleAvailabilityChange(availability?.id, e?.target?.checked)}
              />
            ))}
          </div>
        </div>

        {/* Rating */}
        <div>
          <h3 className="font-heading font-medium text-foreground mb-3">Minimum Rating</h3>
          <div className="space-y-2">
            {[4, 3, 2, 1]?.map((rating) => (
              <button
                key={rating}
                onClick={() => handleRatingChange(rating)}
                className={`flex items-center space-x-2 w-full p-2 rounded-lg transition-wellness ${
                  filters?.minRating === rating
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`}
              >
                <div className="flex items-center space-x-1">
                  {[...Array(5)]?.map((_, i) => (
                    <Icon
                      key={i}
                      name="Star"
                      size={14}
                      className={i < rating ? 'text-accent fill-current' : 'text-muted'}
                    />
                  ))}
                </div>
                <span className="text-sm">& up</span>
              </button>
            ))}
          </div>
        </div>

        {/* Member Tier Benefits */}
        {userTier && (
          <div className="bg-primary/5 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Award" size={16} className="text-primary" />
              <span className="font-medium text-primary">{userTier} Member Benefits</span>
            </div>
            <div className="text-sm text-muted-foreground">
              {userTier === 'Intro' && 'Enjoy 5% discount on all products'}
              {userTier === 'All Access' && 'Enjoy 10% discount + free shipping'}
              {userTier === 'Flex' && 'Enjoy 15% discount + priority support'}
              {userTier === 'Premium' && 'Enjoy 20% discount + exclusive access'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterPanel;